import { useCallback, useMemo, useRef } from 'react';
import { OptionType } from '@cx/ui';
import {
  selectors,
  useApi,
  useAppActions,
  useAppContext,
  useAppState,
} from 'store';
import { DataDrivenFiltersOptionType } from 'types';
import _debounce from 'lodash/debounce';
import { useDataDrivenFilterContext } from '../context/dataDrivenFiltersContext';

export type UseDataDrivenFilterOptionProps = {
  selectedPartitionIndex: number;
  selectedFilterIndex: number;
};

const adaptOptions = (options: DataDrivenFiltersOptionType) =>
  options?.Options?.map((option) => ({
    label: option.DisplayName,
    value: option.Value,
  }));

const defaultFilterOption = (item: OptionType, searchText: string) =>
  item?.label?.toLowerCase?.()?.includes?.(searchText?.toLowerCase());

/**
 * Handle the logic for the enterprise filter option. It connects the UI with the store.
 * @param {UseDataDrivenFilterOptionProps} param0
 * @returns
 */
const useDataDrivenFiltersOption = ({
  selectedPartitionIndex,
  selectedFilterIndex,
}: UseDataDrivenFilterOptionProps) => {
  const { optionsDebounceTime = 500, optionsFilterOption } =
    useDataDrivenFilterContext();
  const api = useApi();
  const context = useAppContext();
  const { updateDataDrivenFilterOption } = useAppActions();
  const currentSelectedFilter = useAppState(
    selectors.dataDrivenFilters.getSelectedFilterByIndex({
      selectedPartitionIndex,
      selectedFilterIndex,
    })
  );
  const selectedPartition = useAppState(
    selectors.dataDrivenFilters.getSelectedPartitionByIndex(
      selectedPartitionIndex
    )
  );
  const selectedOptions = currentSelectedFilter?.data?.values || [];
  const adaptedOptions = useMemo(
    () => adaptOptions(currentSelectedFilter?.data?.options),
    [currentSelectedFilter?.data?.options?.Options]
  );
  const handleOptionSelected = (option: OptionType | OptionType[]) => {
    updateDataDrivenFilterOption({
      selectedPartitionIndex,
      selectedFilterIndex,
      filter: {
        data: {
          values: Array.isArray(option) ? option : [option],
        },
      },
    });
  };

  /**
   * Fetches new options based on the provided search text and executes a callback function with the results.
   */
  const getNewOptions = async (searchText: string) => {
    const responseOptions = await api.getDataDrivenFiltersOptions({
      dimensionId: currentSelectedFilter.data.dimension.FieldGuid,
      partitionId: selectedPartition.data.partition.PublicKey,
      FilterBarValues: context?.getFilterBarData?.(context?.getState?.()),
      searchText,
    });
    return adaptOptions(responseOptions);
  };

  /**
   * Return a debounce function for getting new options from backend
   */
  const handleGetNewOptionsDebounceFn = useRef(
    _debounce(
      (searchText: string, callback: any) =>
        getNewOptions(searchText).then(callback),
      optionsDebounceTime
    )
  );

  /**
   * Filter option to execute when the options does not have backend typeahead
   */
  const filterOption = useCallback(optionsFilterOption || defaultFilterOption, [
    optionsFilterOption,
  ]);

  const handleLoadOptions = (searchText: string, callback: any) => {
    handleGetNewOptionsDebounceFn.current.cancel();
    if (currentSelectedFilter?.data?.options?.BackEndTypeAhead) {
      handleGetNewOptionsDebounceFn.current(searchText, callback);
    } else {
      callback(
        adaptedOptions?.filter?.((item) => filterOption(item, searchText))
      );
    }
  };

  return {
    options: adaptedOptions,
    selectedOptions,
    currentSelectedFilter,
    selectedPartition,
    onOptionSelected: handleOptionSelected,
    onLoadOptions: handleLoadOptions,
  };
};

export default useDataDrivenFiltersOption;
